import { useRef, useState, useEffect } from "react";

/**
 * HeroImage Component - Optimized for LCP (Solution C: Lazy Video Load)
 * Shows existing static image immediately (LCP element), lazy loads video after
 */
export const HeroImage = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoadVideo(true);
          observer.disconnect();
        }
      },
      { rootMargin: '50px' }
    );

    const videoElement = videoRef.current;
    if (videoElement) observer.observe(videoElement);

    return () => observer.disconnect();
  }, []);

  // Detect viewport size when video should load
  useEffect(() => {
    if (shouldLoadVideo) {
      setIsDesktop(window.matchMedia('(min-width: 1024px)').matches);
    }
  }, [shouldLoadVideo]);

  return (
    <div className="absolute inset-0">
      {/* Static image as LCP element - loads first with high priority */}
      <img
        src="/lovable-uploads/hero-mobile-800x365.webp"
        srcSet="
          /lovable-uploads/hero-mobile-800x365.webp 800w,
          /lovable-uploads/hero-tablet-1200x548.webp 1200w,
          /lovable-uploads/hero-desktop-1600x730.webp 1600w
        "
        sizes="100vw"
        alt="Romantisk bröllopsmiljö med brudpar och vacker dekoration - börja planera ert drömbröllop"
        className="w-full h-full absolute z-[1] object-cover object-center"
        style={{ filter: "brightness(0.7)" }}
        loading="eager"
        fetchPriority="high"
        decoding="async"
        width="1600"
        height="730"
      />

      {/* Video loads after LCP - non-blocking, fades in when ready */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        preload="none"
        className="w-full h-full absolute z-[2] object-cover object-center opacity-0 transition-opacity duration-1000"
        onLoadedData={(e) => {
          // Smooth fade-in after video loads (happens after LCP)
          const videoElement = e.currentTarget;
          if (videoElement) {
            setTimeout(() => {
              videoElement.style.opacity = '1';
            }, 100);
          }
        }}
        aria-hidden="true"
      >
        {/* Desktop video sources */}
        {shouldLoadVideo && isDesktop && (
          <>
            <source src="/videos/hero-wedding-desktop.webm" type="video/webm" />
            <source src="/videos/hero-wedding-desktop.mp4" type="video/mp4" />
          </>
        )}
        {/* Mobile video sources */}
        {shouldLoadVideo && !isDesktop && (
          <>
            <source src="/videos/hero-wedding-mobile.webm" type="video/webm" />
            <source src="/videos/hero-wedding-mobile.mp4" type="video/mp4" />
          </>
        )}
      </video>
    </div>
  );
};
